import { useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import SelectSearchHeader from 'TeamModule/components/Filters/SelectSearchHeader';

export type SearchableOption = { value: string; label: string };

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  labelId?: string;
  placeholder?: string;
  fullWidth?: boolean;
  variant?: 'outlined' | 'standard' | 'filled';
  getOptions: (queryLC: string) => SearchableOption[];
  disableMenuMargin?: boolean;
  menuMaxHeight?: number;
  hideAllOption?: boolean;
  /**
   * Must be passed explicitly — a wrapping `fieldset[disabled]` will NOT disable this.
   * MUI's Select is a `div[role=combobox]`, not a listed form control, so it stays
   * clickable inside a disabled fieldset; only its hidden value-carrier input is affected.
   */
  disabled?: boolean;
};

export const SearchableSelect = ({
  value,
  onChange,
  label = '',
  labelId = 'searchable-select-label',
  placeholder = 'Search...',
  fullWidth = true,
  variant = 'outlined',
  getOptions,
  disableMenuMargin = false,
  menuMaxHeight = 316,
  hideAllOption = false,
  disabled = false,
}: Props) => {
  const [query, setQuery] = useState('');
  const queryLC = useMemo(() => query.trim().toLocaleLowerCase(), [query]);
  const options = useMemo(() => getOptions(queryLC), [getOptions, queryLC]);

  return (
    <FormControl fullWidth={fullWidth} variant={variant} disabled={disabled}>
      {label ? <InputLabel id={labelId}>{label}</InputLabel> : null}
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={(e) => onChange(String(e.target.value || ''))}
        onClose={() => setQuery('')}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          MenuListProps: {
            onMouseDown: (e: React.MouseEvent<HTMLUListElement>) => e.stopPropagation(),
            style: {
              border: '1px solid rgba(0,0,0,.1)',
              borderRadius: '5px',
            },
          },
          PaperProps: {
            style: {
              marginTop: disableMenuMargin ? 0 : undefined,
              maxHeight: menuMaxHeight,
              border: '1px solid rgba(0,0,0,.1)',
              borderRadius: '5px',
            },
          },
        }}
      >
        <SelectSearchHeader placeholder={placeholder} value={query} onChange={setQuery} autoFocus />
        {!hideAllOption && <MenuItem value="">All</MenuItem>}
        {options.map(({ value: v, label: l }) => (
          <MenuItem key={v} value={v}>
            {l}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SearchableSelect;
